import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { providerThemeApi } from '../../services/api';
import { ExpertCard } from '../ExpertCard';
import type { Expert, ExpertType } from '../../utils/expertProfile';
import { CompanyThemeEditor } from './CompanyThemeEditor';

interface ProviderThemeEditorProps {
  type: ExpertType;
  /** Admin edits this user's theme; without it, the signed-in provider's own. */
  userId?: string;
  /** Area and languages for the preview card, when known. */
  profile?: Pick<Expert, 'geographicArea' | 'languages'> | null;
}

/**
 * A lawyer's or inspector's theme (ClickUp 869fa6m3u): the company theme
 * editor against their endpoints, previewed as their expert card in Extern
 * hjälp, which is where beställare and hantverkare see it.
 */
export function ProviderThemeEditor({ type, userId, profile }: ProviderThemeEditorProps) {
  const { t } = useTranslation();
  // A picture of the card, not a working one: inert keeps its buttons out of
  // the tab order (React's typings do not know the attribute yet).
  const previewRef = useRef<HTMLDivElement>(null);
  useEffect(() => { previewRef.current?.setAttribute('inert', ''); });
  const client = useMemo(
    () => (userId ? providerThemeApi.forAdmin(type, userId) : providerThemeApi.forSelf(type)),
    [type, userId]
  );
  return (
    <CompanyThemeEditor
      client={client}
      infoText={t(userId ? 'settings.theme.provider.infoAdmin' : 'settings.theme.provider.info')}
      renderPreview={({ colors, name, logoSrc }) => (
        <div className="provider-theme-preview" data-testid="provider-theme-preview">
          <p className="small text-muted mb-2">{t(userId ? 'settings.theme.provider.previewHintAdmin' : 'settings.theme.provider.previewHint')}</p>
          <div className="provider-theme-preview-card" ref={previewRef}>
            <ExpertCard
              provider={{
                id: 'preview', name, availability: true, specialties: [],
                geographicArea: profile?.geographicArea ?? '', languages: profile?.languages ?? [],
              } as unknown as Expert}
              type={type}
              layout="card"
              action={{ label: t('marketplace.provider.sendRequest'), icon: 'send', onClick: () => undefined }}
              expanded={false}
              onToggleProfile={() => undefined}
              brand={{ headingColor: colors.headingColor, accentColor: colors.accentColor, logoSrc }}
            />
          </div>
        </div>
      )}
    />
  );
}
